import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import React, { ComponentProps, useState } from 'react'
import { ValueNarrativeSelectorItem } from './ValueNarrativeSelectorItem'
import { SvgIcon } from '@mui/material'
import { ReactComponent as RightArrowIcon } from '../../../../assets/images/arrows/right.svg'
import { ValueNarrative, ValueNarrativeCategory } from '../../queries/useGetValueNarrativesQuery'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'

type Props = {
    isAIEnabled: boolean
    selectedCategory: ValueNarrativeCategory
    selectedNarrative: ValueNarrative
    handleSelectNarrative: (categoryId: string, narrativeId: string) => void
    colorBySubpopulation: string
}

export const ValueNarrativeSelector = ({
    isAIEnabled,
    selectedCategory,
    selectedNarrative,
    handleSelectNarrative,
}: Props) => {
    const [isAILoading, setIsAILoading] = useState(false)
    const [isAIGeneratedValueStatement, setIsASGeneratedValueStatement] = useState(false)

    const [isEditMode, setIsEditMode] = useState(false)

    const selectedNarrativeIndex = selectedCategory.narratives.findIndex(
        (narrative) => narrative._id === selectedNarrative._id
    )

    const selectNarrative = (narrativeIndex) => {
        const categoryId = selectedCategory._id
        const narrativeId = selectedCategory.narratives[narrativeIndex]._id

        handleSelectNarrative(categoryId, narrativeId)

        setIsEditMode(false)
        setIsASGeneratedValueStatement(false)
    }

    const selectPreviousNarrative = () => {
        selectNarrative(
            (selectedNarrativeIndex - 1 + selectedCategory.narratives.length) % selectedCategory.narratives.length
        )
    }

    const selectNextNarrative = () => {
        selectNarrative((selectedNarrativeIndex + 1) % selectedCategory.narratives.length)
    }

    const canSelectOtherNarrative =
        !selectedNarrative.archived && !isAILoading && !isAIGeneratedValueStatement && !isEditMode

    const hasMoreThanOneNarrative = selectedCategory.narratives.length > 1

    const prevNarrative = selectedCategory.narratives[selectedNarrativeIndex - 1]
    const hasPrevNarrative = !!prevNarrative
    const canSelectPrevNarrative = hasPrevNarrative && canSelectOtherNarrative

    const nextNarrative = selectedCategory.narratives[selectedNarrativeIndex + 1]
    const hasNextNarrative = !!nextNarrative
    const canSelectNextNarrative = canSelectOtherNarrative && hasNextNarrative

    const iconButtonSx: ComponentProps<typeof IconButton>['sx'] = (theme) => ({
        color: theme.palette.figma.primary,
        marginInline: '-8px',
        '&.Mui-disabled': {
            color: theme.palette.figma.grayscale.tier5,
        },
    })

    return (
        <Grid container width="100%" position="relative">
            <Grid container width="100%" columnGap={2} flexWrap="nowrap">
                {canSelectOtherNarrative && hasMoreThanOneNarrative && (
                    <Grid item mt={2} ml={-0.5}>
                        <Tooltip title={prevNarrative?.name} arrow enterDelay={600}>
                            <IconButton
                                onClick={selectPreviousNarrative}
                                sx={iconButtonSx}
                                disabled={!canSelectPrevNarrative}
                                disableRipple
                            >
                                <SvgIcon
                                    component={RightArrowIcon}
                                    inheritViewBox
                                    fontSize="small"
                                    color="inherit"
                                    sx={{
                                        transform: 'rotate(180deg)',
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                )}
                <Grid item xs>
                    <ValueNarrativeSelectorItem
                        selectedNarrative={selectedNarrative}
                        isAIEnabled={isAIEnabled}
                        isAILoading={isAILoading}
                        setIsAILoading={setIsAILoading}
                        isAIGeneratedValueStatement={isAIGeneratedValueStatement}
                        setIsASGeneratedValueStatement={setIsASGeneratedValueStatement}
                        isEditMode={isEditMode}
                        setIsEditMode={setIsEditMode}
                    />
                </Grid>
                {canSelectOtherNarrative && hasMoreThanOneNarrative && (
                    <Grid item mt={2}>
                        <Tooltip title={nextNarrative?.name} arrow enterDelay={600}>
                            <IconButton
                                onClick={selectNextNarrative}
                                sx={iconButtonSx}
                                disabled={!canSelectNextNarrative}
                                disableRipple
                            >
                                <SvgIcon component={RightArrowIcon} inheritViewBox fontSize="small" color="inherit" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>
            <Box
                sx={{
                    height: 0,
                    width: 'calc(100% + 75px)',
                    marginLeft: '-25px',
                    marginTop: isEditMode ? '12px' : '-12px',
                    borderBottom: '1px solid',
                    borderBottomColor: 'figma.grayscale.stroke',
                }}
            />
        </Grid>
    )
}
